<script setup>
import { computed } from 'vue'

/**
 * Numeric stat card with a big orange teko-font value and a small mono
 * uppercase label below. The recurring "HUD" pattern used across the home
 * map overlay (Lokací/Galerií/Fotografů) and the public profile
 * (galleries/locations/photos for photographers, vehicles/saved/tagged for
 * riders).
 *
 * Two variants control background - `glass` for overlay-over-imagery (semi-
 * transparent dark with blur), `surface` for page-level layouts (solid
 * elevated surface). Alignment defaults to "responsive" (centered on
 * mobile, left-aligned on desktop) which matches the most common usage;
 * override to `center` for HUD overlays where the card stays compact on
 * every breakpoint.
 *
 * Examples:
 *   <StatCard :value="20" label="Lokací" variant="glass" align="center" />
 *   <StatCard :value="totalStats.galleries" label="Galerií" />
 */
const props = defineProps({
    value: {
        type: [Number, String],
        required: true,
    },
    label: {
        type: String,
        required: true,
    },
    variant: {
        type: String,
        default: 'surface',
        validator: (v) => ['surface', 'glass'].includes(v),
    },
    align: {
        type: String,
        default: 'responsive',
        validator: (v) => ['responsive', 'center', 'start'].includes(v),
    },
})

// `glass` is dark + heavy blur for layering over MapLibre / images.
// `surface` is the elevated dark token, no transparency - for over-page use.
const VARIANT_CLASSES = {
    surface: 'bg-surface-container-low',
    glass: 'bg-zinc-950/70',
}

// `responsive` = center on mobile, left on desktop (cards in a 3-col grid
// look cleaner left-aligned on wide viewports). `center` and `start` are
// breakpoint-agnostic overrides.
const ALIGN_CLASSES = {
    responsive: 'items-center md:items-start',
    center: 'items-center',
    start: 'items-start',
}

const wrapperClass = computed(() => [
    'border-primary-container/30 flex flex-col rounded-md border px-4 py-4 backdrop-blur-md md:px-5',
    VARIANT_CLASSES[props.variant],
    ALIGN_CLASSES[props.align],
])
</script>

<template>
    <div :class="wrapperClass">
        <span class="teko-font text-primary-container text-3xl leading-none font-black md:text-4xl">
            {{ value }}
        </span>
        <span class="mt-1 font-mono text-[10px] tracking-widest text-zinc-400 uppercase">
            {{ label }}
        </span>
    </div>
</template>
